import { useFormikContext } from 'formik';
import clsx from 'clsx';

import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';
import { FormControl } from '@@/form-components/FormControl';
import { InputList } from '@@/form-components/InputList';
import { type ItemError } from '@@/form-components/InputList/InputList';
import { Input } from '@@/form-components/Input';
import { Badge } from '@@/Badge';
import { Tooltip } from '@@/Tip/Tooltip';
import { FormError } from '@@/form-components/FormError';
import { isArrayErrorType } from '@@/form-components/formikUtils';
import { ButtonSelector } from '@@/form-components/ButtonSelector/ButtonSelector';

import { DurationInput } from './DurationInput';
import { DockerCleanupFormValues } from './types';
import { imageHasExplicitTag } from './imageHasExplicitTag';
import { imageHasRegistryHost } from './imageHasRegistryHost';

export function DockerCleanupFormFields() {
  const { values, errors, setFieldValue } =
    useFormikContext<DockerCleanupFormValues>();

  return (
    <>
      <FormSection title="Cleanup interval">
        <FormControl
          label="Cleanup interval"
          tooltip="How often the agent runs a cleanup cycle."
          errors={errors.cleanupInterval}
          required
          inputId="cleanupInterval"
        >
          <div className="max-w-xs">
            <DurationInput
              id="cleanupInterval"
              value={values.cleanupInterval}
              onChange={(v) => setFieldValue('cleanupInterval', v)}
              min={1}
            />
          </div>
        </FormControl>
      </FormSection>

      <FormSection title="Age-based cleanup">
        <p className="text-muted">Automatically remove old unused images.</p>
        <SwitchField
          checked={values.cleanOldImages}
          onChange={(value) => setFieldValue('cleanOldImages', value)}
          label="Enable age-based cleanup"
          labelClass="col-sm-3 col-lg-2"
          fieldClass="!mb-4"
          data-cy="cleanup-enable-old-images"
        />

        {values.cleanOldImages && (
          <FormControl
            label="Max image age"
            tooltip="Unused images older than this are forcibly removed regardless of disk usage."
            errors={errors.imageMaximumGCAge}
            required
            inputId="imageMaximumGCAge"
          >
            <div className="max-w-xs">
              <DurationInput
                id="imageMaximumGCAge"
                value={values.imageMaximumGCAge}
                onChange={(v) => setFieldValue('imageMaximumGCAge', v)}
                min={1}
              />
            </div>
          </FormControl>
        )}
      </FormSection>

      <FormSection title="Storage-limit cleanup">
        <p className="text-muted">
          Remove unused images when disk usage gets too high.
        </p>
        <SwitchField
          checked={values.cleanImagesAtStorageLimit}
          onChange={(value) =>
            setFieldValue('cleanImagesAtStorageLimit', value)
          }
          label="Enable storage-limit cleanup"
          labelClass="col-sm-3 col-lg-2"
          fieldClass="!mb-4"
          data-cy="cleanup-enable-storage-limit"
        />
        {/* 'At least one cleanup type must be enabled' error */}
        {!!errors.cleanOldImages && (
          <FormError className="mb-4">{errors.cleanOldImages}</FormError>
        )}

        {values.cleanImagesAtStorageLimit && (
          <>
            <FormControl
              label="High threshold (%)"
              tooltip="Start removing images when disk usage exceeds this percentage."
              errors={errors.highThresholdPercent}
              required
              inputId="highThresholdPercent"
            >
              <div className="max-w-xs">
                <Input
                  type="number"
                  value={values.highThresholdPercent}
                  onChange={(e) =>
                    setFieldValue(
                      'highThresholdPercent',
                      Number(e.target.value)
                    )
                  }
                  min={values.lowThresholdPercent}
                  max={100}
                  data-cy="cleanup-high-threshold"
                  id="highThresholdPercent"
                />
              </div>
            </FormControl>

            <FormControl
              label="Low threshold (%)"
              tooltip="Stop removing images when disk usage drops below this percentage."
              errors={errors.lowThresholdPercent}
              required
              inputId="lowThresholdPercent"
            >
              <div className="max-w-xs">
                <Input
                  type="number"
                  value={values.lowThresholdPercent}
                  onChange={(e) =>
                    setFieldValue('lowThresholdPercent', Number(e.target.value))
                  }
                  min={0}
                  max={values.highThresholdPercent}
                  data-cy="cleanup-low-threshold"
                  id="lowThresholdPercent"
                />
              </div>
            </FormControl>

            <SwitchField
              checked={values.clearBuildCache}
              onChange={(value) => setFieldValue('clearBuildCache', value)}
              label="Clear Docker build cache"
              tooltip="Prune the Docker build cache when disk usage exceeds the high threshold."
              labelClass="col-sm-3 col-lg-2"
              fieldClass="!mb-4"
              data-cy="cleanup-clear-build-cache"
            />

            <FormControl
              label="Minimum image age"
              tooltip="Images newer than this are never removed by storage-limit cleanup."
              errors={errors.minAge}
              required
              inputId="minAge"
            >
              <div className="max-w-xs">
                <DurationInput
                  id="minAge"
                  value={values.minAge}
                  onChange={(v) => setFieldValue('minAge', v)}
                />
              </div>
            </FormControl>

            <FormControl
              label="Limit images removed per interval"
              tooltip="Maximum images removed per storage-limit cleanup cycle."
              errors={errors.maxImagesPerInterval}
              inputId="maxImagesPerInterval"
            >
              <div className="flex max-w-xs items-center gap-1">
                <ButtonSelector<string>
                  options={[
                    { label: 'No limit', value: 'noLimit' },
                    { label: 'Limit', value: 'limit' },
                  ]}
                  onChange={(value) => {
                    if (value === 'noLimit') {
                      setFieldValue('maxImagesPerInterval', 0);
                    } else if (
                      value === 'limit' &&
                      values.maxImagesPerInterval === 0
                    ) {
                      setFieldValue('maxImagesPerInterval', 10);
                    }
                  }}
                  value={
                    values.maxImagesPerInterval === 0 ? 'noLimit' : 'limit'
                  }
                  data-cy="cleanup-max-images-per-interval-selector"
                ></ButtonSelector>
                <div
                  className={clsx('inline-flex items-center gap-1', {
                    invisible: values.maxImagesPerInterval === 0,
                  })}
                >
                  to{' '}
                  <Input
                    type="number"
                    value={values.maxImagesPerInterval}
                    onChange={(e) =>
                      setFieldValue(
                        'maxImagesPerInterval',
                        Number(e.target.value)
                      )
                    }
                    min={1}
                    data-cy="cleanup-max-images-per-interval"
                    id="maxImagesPerInterval"
                  />
                </div>
              </div>
            </FormControl>
          </>
        )}
      </FormSection>

      <FormSection title="Excluded images">
        <p className="text-muted">
          Prevent important images from being removed. Use * and ? as wildcards,
          e.g. my-app-* or my-app:v1.* (requires agent 2.45.2 or later).
        </p>
        <InputList<string>
          value={values.excludedImages}
          onChange={(images) => setFieldValue('excludedImages', images)}
          itemBuilder={() => ''}
          renderItem={(item, onChange, index, _dataCy, error) => (
            <ExcludedImageItem
              item={item}
              onChange={onChange}
              index={index}
              error={error}
            />
          )}
          errors={
            isArrayErrorType<string>(errors.excludedImages)
              ? errors.excludedImages
              : undefined
          }
          addLabel="Add image"
          data-cy="cleanup-excluded-images"
        />
      </FormSection>
    </>
  );
}

function ExcludedImageItem({
  item,
  onChange,
  index,
  error,
}: {
  item: string;
  onChange: (value: string) => void;
  index: number;
  error?: ItemError<string>;
}) {
  const reference = item.trim();
  const hasTag = imageHasExplicitTag(reference);
  const hasRegistryHost = imageHasRegistryHost(reference);

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center gap-2">
        <Input
          value={item}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. harbor.mycloud.io/images/nginx or caddy:latest"
          data-cy={`cleanup-excluded-image-${index}`}
          id={`cleanup-excluded-image-${index}`}
        />
        {reference && !hasTag && (
          <Badge type="muted" className="whitespace-nowrap">
            All tags
          </Badge>
        )}
        {reference && !hasRegistryHost && (
          <Badge className="whitespace-nowrap">
            Docker Hub or local{' '}
            <Tooltip message="Matches images with no registry host, including Docker Hub images and images built locally on the host. To exclude an image from another registry, include the registry host, e.g. harbor.mycloud.io/images/nginx."></Tooltip>
          </Badge>
        )}
      </div>
      {error && <FormError>{error}</FormError>}
    </div>
  );
}
